import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { ETypeLien } from '../entites/enums/type-lien-document';
import { SupabaseService } from './supabase.service';
import { BaseHubService } from './base-hub.service';

export type SihcChannel = 'items' | 'commandes' | 'bontravails' | 'jobs' | 'notifications';

export const SihcEvents = {
    itemsAdd: 'items:Add',
    itemsUpdate: 'items:Update',
    itemsDelete: 'items:Delete',
    commandesAdd: 'commandes:Add',
    commandesUpdate: 'commandes:Update',
    commandesDelete: 'commandes:Delete',
    bonTravailsAdd: 'bontravails:Add',
    bonTravailsUpdate: 'bontravails:Update',
    bonTravailsDelete: 'bontravails:Delete',
    bonTravailsFieldUpdate: 'bontravails:FieldUpdate',
    jobsUpdate: 'jobs:ReceiveJobUpdate',
    notificationReceived: 'notifications:ReceiveNotification',
    lock: 'lock:Lock'
} as const;

export interface ModifyingEmployee {
    employeId: number;
    noEmploye: string;
    nom: string;
    prenom: string;
}

export interface ReponseLock {
    id: string;
    employe: ModifyingEmployee;
}

/**
 * Hub SignalR unifié : une seule connexion WebSocket, abonnement aux canaux
 * via les groupes du `SihcHub` backend (`Subscribe`/`Unsubscribe`).
 *
 * Remplace ItemsHub, CommandesHub, BonTravailHub, JobHub, NotificationHub et
 * DocumentLockHub (1 connexion chacun). Les consommateurs appellent
 * `subscribe(channel)` puis se désabonnent avec `unsubscribe(channel)`;
 * `disconnect()` est réservé au logout (connexion partagée).
 */
@Injectable({
    providedIn: 'root'
})
export class SihcHubService extends BaseHubService {
    private channelRefs = new Map<SihcChannel, number>();
    private lockSubs = new Set<string>();
    private lockSubject = new Subject<ReponseLock>();
    public lock$ = this.lockSubject.asObservable();

    constructor(private supabaseService: SupabaseService) {
        super();
        this.initializeConnection();
    }

    protected getHubUrl(): string {
        return environment.apiBaseUrl + 'updates/sihc';
    }

    protected override async getAccessToken(): Promise<string | null> {
        try {
            return await this.supabaseService.getAccessToken();
        } catch (error) {
            console.error('Failed to get access token for SihcHub:', error);
            return null;
        }
    }

    protected registerServerEvents(): void {
        this.hubConnection.off(SihcEvents.lock);
        this.hubConnection.on(SihcEvents.lock, (response: ReponseLock) => {
            this.lockSubject.next(response);
        });
    }

    protected refreshDataAfterReconnection(longDisconnection: boolean): void {
        // Réabonne les canaux + verrous actifs après reconnexion.
        this.channelRefs.forEach((_count, channel) => {
            if (this.isConnected()) {
                this.hubConnection.invoke('Subscribe', channel)
                    .catch(err => console.error(`Resubscribe to ${channel} failed`, err));
            }
        });
        this.lockSubs.forEach(sub => {
            const [typeStr, id] = sub.split('-');
            const eTypeLien = ETypeLien[typeStr as keyof typeof ETypeLien];
            if (this.isConnected()) {
                this.hubConnection.invoke('SubscribeToIdAndType', eTypeLien, id)
                    .catch(err => console.error('Resubscribe to lock failed', err));
            }
        });
        if (longDisconnection) {
            console.log('Long disconnection detected for SihcHub, full refresh recommended');
        }
    }

    public override async connect(): Promise<void> {
        await super.connect();
        // La connexion peut déjà être établie (partagée) : (ré)abonne les canaux actifs.
        if (this.isConnected()) {
            const pending: Promise<void>[] = [];
            this.channelRefs.forEach((_count, channel) => {
                pending.push(
                    this.hubConnection.invoke('Subscribe', channel)
                        .catch(err => console.error(`Subscribe to ${channel} failed`, err))
                );
            });
            await Promise.all(pending);
        }
    }

    /**
     * Abonne la connexion partagée à un canal (compteur de références).
     */
    public async subscribe(channel: SihcChannel): Promise<void> {
        this.channelRefs.set(channel, (this.channelRefs.get(channel) ?? 0) + 1);
        await this.connect();
        if (this.isConnected()) {
            await this.hubConnection.invoke('Subscribe', channel)
                .catch(err => console.error(`Subscribe to ${channel} failed`, err));
        }
    }

    /**
     * Désabonne un canal (décrémente le compteur ; `Unsubscribe` serveur au dernier).
     */
    public unsubscribe(channel: SihcChannel): void {
        const count = (this.channelRefs.get(channel) ?? 1) - 1;
        if (count <= 0) {
            this.channelRefs.delete(channel);
            if (this.isConnected()) {
                this.hubConnection.invoke('Unsubscribe', channel)
                    .catch(err => console.error(`Unsubscribe from ${channel} failed`, err));
            }
        } else {
            this.channelRefs.set(channel, count);
        }
    }

    /**
     * Subscribes to lock events for a specific document.
     */
    public subscribeLock(eTypeLien: ETypeLien, id: string): void {
        const subKey = `${ETypeLien[eTypeLien]}-${id}`;
        this.lockSubs.add(subKey);

        if (this.isConnected()) {
            this.hubConnection.invoke('SubscribeToIdAndType', eTypeLien, id)
                .catch(err => console.error('Error while subscribing to lock:', err));
        }
    }

    /**
     * Unsubscribes from lock events for a specific document.
     */
    public unsubscribeLock(eTypeLien: ETypeLien, id: string): void {
        const subKey = `${ETypeLien[eTypeLien]}-${id}`;
        this.lockSubs.delete(subKey);

        if (this.isConnected()) {
            this.hubConnection.invoke('UnsubscribeFromIdAndType', eTypeLien, id)
                .catch(err => console.error('Error while unsubscribing from lock:', err));
        }
    }

    /**
     * Sends a lock pulse to the server to maintain a lock.
     */
    public sendLock(eTypeLien: ETypeLien, id: string): void {
        if (this.isConnected()) {
            this.hubConnection.invoke('SendLock', eTypeLien, id)
                .catch(err => console.error('Error while sending lock:', err));
        }
    }
}
